import React from 'react';
import { Link } from 'react-router-dom';
import useEmblaCarousel from 'embla-carousel-react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { getAdSlides } from '../../data/specials';
import { OfferCountdown } from '../specials/OfferCountdown';
import { trackAdClick } from '../../utils/siteTracking';

const ROTATE_MS = 5000;

const ArrowBtn = ({ dir, onClick }) => {
  const Icon = dir === 'prev' ? ArrowLeft : ArrowRight;
  return (
    <button onClick={onClick} aria-label={dir === 'prev' ? 'Previous offer' : 'Next offer'}
      className="w-11 h-11 rounded-full border border-slate-300 bg-white text-slate-800 flex items-center justify-center hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-colors"
      data-testid={`offer-slider-${dir}`}>
      <Icon size={18} />
    </button>
  );
};

const useAutoRotate = (api, paused) => {
  React.useEffect(() => {
    if (!api || paused || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const t = setInterval(() => api.scrollNext(), ROTATE_MS);
    return () => clearInterval(t);
  }, [api, paused]);
};

export const OfferSlider = () => {
  const slides = getAdSlides();
  const [ref, api] = useEmblaCarousel({ loop: true, align: 'start' });
  const [selected, setSelected] = React.useState(0);
  const [paused, setPaused] = React.useState(false);
  useAutoRotate(api, paused);

  React.useEffect(() => {
    if (!api) return undefined;
    const onSelect = () => setSelected(api.selectedScrollSnap());
    const onDrag = () => setPaused(true);
    api.on('select', onSelect).on('pointerDown', onDrag);
    return () => { api.off('select', onSelect).off('pointerDown', onDrag); };
  }, [api]);

  if (!slides.length) return null;

  return (
    <div onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocusCapture={() => setPaused(true)}
      aria-roledescription="carousel" aria-label="Current special offers" data-testid="offer-slider">
      <div className="overflow-hidden -mx-5 px-5 sm:mx-0 sm:px-0" ref={ref}>
        <div className="flex -ml-4 sm:-ml-6">
          {slides.map(({ special, image, v }, i) => (
            <div key={`${special.id}-${v}`} className="min-w-0 shrink-0 grow-0 basis-[88%] sm:basis-1/2 lg:basis-1/3 pl-4 sm:pl-6"
              role="group" aria-roledescription="slide" aria-label={`${i + 1} of ${slides.length}: ${special.title}`} data-testid={`offer-slide-${i}`}>
              <Link to={`/specials/${special.slug}`} onClick={() => trackAdClick(special.slug, image, 'home_slider')} className="group block rounded-3xl overflow-hidden bg-white shadow-sm hover:shadow-xl transition-shadow duration-300" draggable={false}>
                <div className="aspect-square overflow-hidden bg-stone-200">
                  <img src={image} alt={`${special.title} - ComfortMaster Heating & Air special offer`} draggable={false}
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" loading={i < 3 ? 'eager' : 'lazy'} />
                </div>
                <div className="flex flex-col items-start gap-2 px-5 py-4">
                  <OfferCountdown validUntil={special.validUntil} testId={`offer-slide-countdown-${i}`} />
                  <span className="font-heading font-bold text-lg text-slate-900 leading-tight line-clamp-2 min-h-[2.5em]">{special.title}</span>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between gap-4 mt-6">
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Choose offer">
          {slides.map((s, i) => (
            <button key={i} role="tab" aria-selected={selected === i} aria-label={`Go to offer ${i + 1}`} onClick={() => api?.scrollTo(i)}
              className={`h-2.5 rounded-full transition-[width,background-color] duration-300 ${selected === i ? 'w-8 bg-emerald-600' : 'w-2.5 bg-slate-300 hover:bg-slate-400'}`}
              data-testid={`offer-slider-dot-${i}`} />
          ))}
        </div>
        <div className="flex gap-2">
          <ArrowBtn dir="prev" onClick={() => api?.scrollPrev()} />
          <ArrowBtn dir="next" onClick={() => api?.scrollNext()} />
        </div>
      </div>
    </div>
  );
};
